import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { payHeadsApi } from '../api/payHeadsApi';
import { appToast } from '../../../../components/AppToast';

import { listAccountHeads } from '../../../masters/acheads/heads/api/acHeadApi';

const DEFAULT_FORM = {
  name: '',
  alias: '',
  accountHead: '',
  acHeadId: 0,
  payslipDesc: '',
  payHeadType: 'Earning',
  category: 'Select',
  incomeType: 'Fixed',
  calculationType: 'On Attendance',
  attendanceLeaveWithPay: 'Not Applicable',
  calculationPeriodType: 'Months',
  basisOfCalcPerDay: 'As Per Calendar Period',
  userDefinedDays: 0,
  includeInSalarySlip: 'Yes',
  effectNetSalary: 'Yes',
  percentage: 0,
  value: 0,
  formulas: [],
  slabs: [],
};

export function usePayHeadsEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [availablePayHeads, setAvailablePayHeads] = useState([]);
  const [accountHeadOptions, setAccountHeadOptions] = useState([]);
  const [dynamicOptions, setDynamicOptions] = useState({
    payHeadTypes: [],
    calculationTypes: [],
    incomeTypes: [],
    calculationPeriods: [],
    basisOfCalcPerDay: [],
    attendanceLeaveWithPay: [],
    categories: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load available pay heads for formula dependency linking, dynamic dropdowns, and account heads from AcHeads master
  const loadOptions = useCallback(async () => {
    try {
      const [optsResult, headsResult] = await Promise.allSettled([
        payHeadsApi.getPayHeadOptions(id || undefined),
        listAccountHeads(),
      ]);

      if (optsResult.status === 'fulfilled') {
        const d = optsResult.value || {};
        setAvailablePayHeads(d.available_pay_heads || []);
        setDynamicOptions({
          payHeadTypes: d.pay_head_types || [],
          calculationTypes: d.calculation_types || [],
          incomeTypes: d.income_types || [],
          calculationPeriods: d.calculation_periods || [],
          basisOfCalcPerDay: d.basis_of_calculation_per_day || [],
          attendanceLeaveWithPay: d.attendance_leave_with_pay || [],
          categories: d.categories || [],
        });
      }

      if (headsResult.status === 'fulfilled' && Array.isArray(headsResult.value)) {
        const formatted = headsResult.value.map((h) => ({
          value: h.name,
          label: `${h.name}${h.group ? ` (${h.group})` : ''}`,
          id: h.id,
          alias: h.alias || '',
        }));
        setAccountHeadOptions(formatted);
      }
    } catch (err) {
      console.error('Failed to load pay head options:', err);
    }
  }, [id]);

  // Load existing pay head detail when in edit mode
  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const found = await payHeadsApi.getPayHeadById(id);
      if (found) {
        setForm({
          ...DEFAULT_FORM,
          ...found,
          formulas: found.formulas || [],
          slabs: found.slabs || [],
        });
      } else {
        appToast.error('Pay Head not found.');
        navigate('/payroll/payheads');
      }
    } catch (err) {
      console.error('Failed to load Pay Head:', err);
      appToast.error('Failed to load Pay Head details.');
      navigate('/payroll/payheads');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadOptions();
    if (isEdit) {
      loadData();
    }
  }, [isEdit, loadData, loadOptions]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccountHeadChange = (val) => {
    const matched = accountHeadOptions.find((o) => o.value === val);
    setForm((prev) => ({
      ...prev,
      accountHead: val,
      acHeadId: matched ? Number(matched.id) || 0 : prev.acHeadId,
      alias: matched?.alias || prev.alias || val,
    }));
  };

  const handleSave = async (options = { exitAfter: true }) => {
    const trimmedName = (form.name || '').trim();
    if (!trimmedName) {
      appToast.error('Pay Head Name is required.');
      return;
    }

    // Validation for computed value
    if (form.calculationType === 'As Computed Value') {
      if (form.slabs.length === 0 && form.formulas.length === 0 && Number(form.percentage || 0) <= 0) {
        appToast.error('Please configure at least one formula dependency or slab bracket for computed value.');
        return;
      }
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        await payHeadsApi.updatePayHead(id, form);
        appToast.success(`Pay Head "${trimmedName}" updated successfully.`);
      } else {
        await payHeadsApi.createPayHead(form);
        appToast.success(`Pay Head "${trimmedName}" created successfully.`);
      }

      if (options.exitAfter) {
        navigate('/payroll/payheads');
      } else {
        setForm(DEFAULT_FORM);
        loadOptions();
      }
    } catch (err) {
      console.error('Save failed:', err);
      const errMsg = err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Failed to save Pay Head.';
      appToast.error(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      form,
      availablePayHeads,
      accountHeadOptions,
      dynamicOptions,
      isEdit,
      id,
      isLoading,
      isSaving,
    },
    actions: {
      patchForm,
      handleAccountHeadChange,
      setForm,
      handleSave,
      navigate,
    },
  };
}
